// Live 2.0 API Client

import type { 
  SimulationStatus, 
  SimulationData,
  CreateSimulationRequest,
  UpdateSimulationRequest 
} from './types'

export class SimulationAPI {
  private baseUrl: string

  constructor(baseUrl?: string) {
    // Use environment variable or relative path for production
    // In production, API is on the same domain, so we use relative paths
    this.baseUrl = baseUrl || import.meta.env.VITE_API_BASE || ''
  }

  private async fetchWithTimeout(url: string, options: RequestInit = {}, timeoutMs = 30000): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
      })
    } finally {
      clearTimeout(timeoutId)
    }
  }

  private async delay(ms: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, ms))
  }

  async createSimulation(request: CreateSimulationRequest): Promise<{ success: boolean; simulation_id?: string; message?: string }> {
    let lastError: Error | null = null

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            config: request.config,
            mode: request.config.mode
          }),
        }, 45000)

        if (response.ok) {
          return response.json()
        }

        // Try to get error details from response
        let errorMessage = `Failed to create simulation: ${response.statusText}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.detail || errorData.message || errorMessage
        } catch {
          // If JSON parsing fails, use status text
        }

        // One retry for transient server-side errors
        if (attempt === 0 && response.status >= 500) {
          await this.delay(800)
          continue
        }

        throw new Error(errorMessage)
      } catch (error: any) {
        const isTransient =
          error?.name === 'AbortError' ||
          (error instanceof TypeError && error.message.includes('Failed to fetch'))

        if (attempt === 0 && isTransient) {
          await this.delay(800)
          continue
        }
        lastError = error instanceof Error ? error : new Error('Failed to create simulation')
        break
      }
    }

    // Fallback: if create response failed but simulation was created, recover current id.
    try {
      const current = await this.getCurrentSimulation()
      if (current.exists && current.simulation_id) {
        const exists = await this.checkSimulationExists(current.simulation_id)
        if (!exists) {
          throw new Error('Current simulation is stale')
        }
        return {
          success: true,
          simulation_id: current.simulation_id,
          message: 'Recovered existing simulation after create failure'
        }
      }
    } catch {
      // no-op
    }

    if (lastError) {
      throw lastError
    }
    throw new Error('Failed to create simulation')
  }

  async getSimulationStatus(id: string): Promise<any> {
    const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/${id}/status`, {}, 20000)

    if (!response.ok) {
      throw new Error(`Failed to get simulation: ${response.statusText}`)
    }
    return response.json()
  }

  async updateSimulation(id: string, request: UpdateSimulationRequest): Promise<any> {
    // Backend doesn't have a direct update endpoint, so we'll use the specific action endpoints
    if (request.status === 'running') {
      return this.startSimulation(id)
    } else if (request.status === 'paused') {
      return this.pauseSimulation(id)
    } else if (request.status === 'stopped') {
      return this.stopSimulation(id)
    }
    
    // If no status change, just return current status
    return this.getSimulationStatus(id)
  }

  async deleteSimulation(id: string): Promise<void> {
    // Backend doesn't have a delete endpoint, so we'll just stop the simulation
    await this.stopSimulation(id)
  }

  async startSimulation(id: string): Promise<any> {
    console.log(`API: Calling startSimulation for ${id}`)
    const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/${id}/start`, {
      method: 'POST',
    }, 30000)

    if (!response.ok) {
      throw new Error(`Failed to start simulation: ${response.statusText}`)
    }

    console.log(`API: startSimulation completed for ${id}`)
    return this.getSimulationStatus(id)
  }

  async pauseSimulation(id: string): Promise<any> {
    const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/${id}/pause`, {
      method: 'POST',
    }, 30000)

    if (!response.ok) {
      throw new Error(`Failed to pause simulation: ${response.statusText}`)
    }

    return this.getSimulationStatus(id)
  }

  async stopSimulation(id: string): Promise<any> {
    const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/${id}/stop`, {
      method: 'POST',
    }, 30000)

    if (!response.ok) {
      throw new Error(`Failed to stop simulation: ${response.statusText}`)
    }

    return this.getSimulationStatus(id)
  }

  async getSimulationData(_id: string): Promise<SimulationData> {
    // Backend doesn't have a direct data endpoint, so we'll return mock data for now
    // In a real implementation, this would come from WebSocket or a different endpoint
    return {
      // Provide empty structures matching expected shape
      particles: { positions: [], attributes: [], active_mask: [] },
      bonds: [],
      energy_field: [],
      metrics: {
        particle_count: 0,
        bond_count: 0,
        cluster_count: 0,
        novelty_rate: 0
      },
      timestamp: Date.now()
    }
  }

  async getSimulations(): Promise<SimulationStatus[]> {
    // Backend doesn't have a list endpoint, so we'll return empty array
    return []
  }

  async getActiveSimulations(): Promise<string[]> {
    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/simulations/active`, {}, 15000)
      if (!response.ok) {
        return []
      }
      const data = await response.json()
      return data.simulations || []
    } catch (error) {
      console.error('Failed to get active simulations:', error)
      return []
    }
  }

  async checkSimulationExists(id: string): Promise<boolean> {
    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/${id}/status`, {}, 10000)
      return response.ok
    } catch (error) {
      return false
    }
  }

  async getCurrentSimulation(): Promise<{ simulation_id: string | null; exists: boolean }> {
    try {
      const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/current`, {}, 10000)
      if (!response.ok) {
        return { simulation_id: null, exists: false }
      }
      return response.json()
    } catch (error) {
      return { simulation_id: null, exists: false }
    }
  }

  async resumeSimulation(id: string): Promise<any> {
    // Backend has separate resume endpoint
    const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/${id}/resume`, { method: 'POST' }, 30000)
    if (!response.ok) throw new Error(`Failed to resume simulation: ${response.statusText}`)
    return this.getSimulationStatus(id)
  }

  async resetSimulation(id: string): Promise<any> {
    const response = await this.fetchWithTimeout(`${this.baseUrl}/simulation/${id}/reset`, { method: 'POST' }, 30000)
    if (!response.ok) throw new Error(`Failed to reset simulation: ${response.statusText}`)
    return this.getSimulationStatus(id)
  }

  async getHealth(): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/`)

    if (!response.ok) {
      throw new Error(`Failed to get health: ${response.statusText}`)
    }

    return response.json()
  }

  async getMetrics(id: string): Promise<any> {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 120000) // 120 second timeout for optimized backend
      
      const response = await fetch(`${this.baseUrl}/simulation/${id}/metrics`, {
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        if (response.status === 0) {
          throw new Error('Network error: Unable to connect to server')
        }
        throw new Error(`Failed to get metrics: ${response.status} ${response.statusText}`)
      }
      return response.json()
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Server may be overloaded or unavailable')
      }
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout: Server is not responding')
      }
      throw error
    }
  }

  async getNovelSubstances(id: string, count = 5): Promise<any> {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 120000) // 120 second timeout for optimized backend
      
      const response = await fetch(`${this.baseUrl}/simulation/${id}/novel-substances?count=${count}`, {
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        if (response.status === 0) {
          throw new Error('Network error: Unable to connect to server')
        }
        throw new Error(`Failed to get novel substances: ${response.status} ${response.statusText}`)
      }
      return response.json()
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Server may be overloaded or unavailable')
      }
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout: Server is not responding')
      }
      throw error
    }
  }

  async saveSnapshot(id: string, filename: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/simulation/${id}/snapshot/save?filename=${encodeURIComponent(filename)}`, { method: 'POST' })
    if (!response.ok) throw new Error(`Failed to save snapshot: ${response.statusText}`)
    return response.json()
  }

  async loadSnapshot(id: string, filename: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/simulation/${id}/snapshot/load?filename=${encodeURIComponent(filename)}`, { method: 'POST' })
    if (!response.ok) throw new Error(`Failed to load snapshot: ${response.statusText}`)
    return response.json()
  }

  async getPerformanceMetrics(id: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/simulation/${id}/performance`)
    if (!response.ok) throw new Error(`Failed to get performance metrics: ${response.statusText}`)
    return response.json()
  }

  async getSubstanceDetails(simulationId: string, substanceId: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/simulation/${simulationId}/substance/${substanceId}/details`)
    if (!response.ok) throw new Error(`Failed to get substance details: ${response.statusText}`)
    return response.json()
  }

  async matchSubstanceToPubchem(simulationId: string, substanceId: string): Promise<any> {
    const response = await fetch(
      `${this.baseUrl}/simulation/${simulationId}/substance/${substanceId}/match`,
      { method: 'POST' }
    )
    if (!response.ok) throw new Error(`Failed to match substance: ${response.statusText}`)
    return response.json()
  }
}
